import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { setBackButtonUrl } from '../uiSlice';
import Board from '../shiftTicTacToe/components/Board';
import TurnTransition from '../shiftTicTacToe/components/TurnTransition';
import { GameHudXPanel, GameHudOPanel } from './GameHud';
import RulesModal from './RulesModal';
import useMultiplayer from '../shiftTicTacToe/hooks/useMultiplayer';
import useSound from '../shiftTicTacToe/hooks/useSound';
import { sendReport } from '../../functions/sendReport';
import { updateScore } from '../../functions/updateScore';
import { fetchReport as fetchReportApi } from '../../functions/fetchReport';
import { savePassPlayResult } from '../../functions/savePassPlayResult';
import { saveComputervsPersonResult } from '../../functions/saveComputervsPersonResult';
import { doc, getDoc } from 'firebase/firestore';
import { database } from '../../database/firebase';

import {
  readPassPlayPlayers,
  winnerLabel,
  computePassPlayPoints,
} from '../passPlayCreate/passPlayStorage';
import {
  writeScoreboardData,
  playersFromUpdateScoreResult,
  resolvePlayerDisplayName,
  isScoreboardGamePrepared,
  readScoreboardMatchSig,
  clearLeaderboardNavigatedFlag,
} from '../leaderboard/scoreboardStorage';
import {
  consumeLobbyRematchPending,
  consumePendingLocalGameReset,
  peekLobbyRematchPending,
  peekPendingLocalGameReset,
} from '../leaderboard/prepareNextGameAfterScoreboard';

import {
  startGame,
  placePiece,
  shiftPiece,
  resetGame,
  exitGame,
  dismissTurnTransition,
  setLobbyPvpSession,
  makeAIMove,
  updateSettings,
  syncGameState,
  selectGameState,
  selectGameMode,
  selectDifficulty,
  selectPlayerRole,
  selectSettings,
  selectIsAIThinking,
  selectShowTurnTransition,
  selectError,
  selectCanMakeMove,
} from '../shiftTicTacToe/store/gameSlice';
import {
  canPlayerShift,
  resolveBlockedShiftTurn,
} from '../shiftTicTacToe/engine/gameLogic';
import '../shiftTicTacToe/shiftTicTacToe.css';
import './game.css';
import {
  clearLocalGameSnapshot,
  isLocalPersistedCategory,
  loadLocalGameSnapshot,
  saveLocalGameSnapshot,
} from './gameLocalStorage';
import { readStoredCategoryMode } from './gameCategorySession';
import {
  getNextStartingPlayer,
  starterStorageKey,
} from './alternateStartStorage';
import { pickRandomTurnMove } from './turnAutoPlay';
import { useTurnCountdown } from './useTurnCountdown';
import { fireGameOverConfetti } from './gameConfetti';
import { store } from '../../admin/store';
import { selectOrganizationId, selectSessionId } from '../../admin/sessionSlice';
import {
  gameStateSignature,
  readLobbyContextFromStorage,
  useLobbyPvpFirestore,
  fetchLobbyLivePlayers,
  fetchLobbyHudIdentities,
  fetchLobbyJoinMeta,
  characterImageSrc,
  aiOpponentImageSrc,
  parseHudCharacter,
  claimLobbyGameOverScores,
  waitForLobbyGameScoresReady,
  isLobbyGameScoresReadyFromDoc,
  persistLobbyPvpScoresToFirestore,
  setLobbyGameStatus,
} from './useLobbyPvpFirestore';

const MODE_LABELS = {
  pvp: 'Player vs Player',
  ai: 'vs Computer',
  online: 'Online Multiplayer',
};

const RESULT_OVERLAY_DELAY_MS = 1500;
const LOBBY_SCORE_SUBMIT_WAIT_MS = 20000;

function extractReportId(result) {
  if (result == null || result === true) return undefined;
  if (typeof result === 'object') {
    return (
      result.reportId ??
      result.report_id ??
      result.id ??
      result.data?.reportId ??
      result.data?.report_id ??
      result.data?.id
    );
  }
  return undefined;
}

function isGuestAuthRole(value) {
  const s = String(value ?? '').trim().toUpperCase();
  return s === 'GUEST_USER' || s === 'GUEST' || s === 'GUESTUSER';
}

/** Extramile auth role for sendReport (guests have no JWT). */
function resolveAuthRole(value) {
  return isGuestAuthRole(value) ? 'GUEST_USER' : 'ORG_USER';
}

function toDurationString(input) {
  if (typeof input === 'string' && /^\d{2}:\d{2}:\d{2}$/.test(input.trim())) {
    return input.trim();
  }
  const totalSeconds = Math.max(0, Number(input) || 0);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

function parseDurationToSeconds(input) {
  if (input == null || input === '') return 0;
  if (typeof input === 'number' && Number.isFinite(input)) {
    return Math.max(0, Math.floor(input));
  }
  const s = String(input).trim();
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) {
    const [h, m, sec] = s.split(':').map((n) => Number(n) || 0);
    return h * 3600 + m * 60 + sec;
  }
  return Math.max(0, Math.floor(Number(s) || 0));
}

function mergeDurationStrings(previous, current) {
  const total = parseDurationToSeconds(previous) + parseDurationToSeconds(current);
  return toDurationString(total);
}

/** Cumulative points, time, reportId from fetchReport (stat_ini or stat_ini_vs_computer by gameMode). */
async function fetchExistingReportStats({
  sessionId,
  organizationId,
  userId,
  token,
  gameMode = 'pvp',
}) {
  const empty = {
    points: 0,
    time: '00:00:00',
    reportId: undefined,
    hasEntry: false,
  };
  try {
    const data = await fetchReportApi({
      sessionId,
      organizationId,
      userId,
      token,
      gameMode,
    });
    if (!data?.success || !data.report) return empty;

    const n = Number(data.report.points);
    const reportIdRaw = data.report.reportId ?? data.report.report_id;
    return {
      points: Number.isFinite(n) ? n : 0,
      time: data.report.time ?? '00:00:00',
      reportId:
        reportIdRaw != null && String(reportIdRaw).trim() !== ''
          ? String(reportIdRaw).trim()
          : undefined,
      hasEntry: true,
    };
  } catch (e) {
    console.warn('fetchReport for cumulative stats:', e);
  }
  return empty;
}

/**
 * 1) fetchReport for gameMode — if DB row exists, merge points/time and pass reportId.
 * 2) sendReport (add or update).
 */
async function fetchMergeAndSendReport({
  sessionId,
  organizationId,
  userId,
  token,
  gameId,
  name,
  role,
  gameMode,
  matchPoints,
  matchTimeSeconds,
}) {
  const {
    points: existingPoints,
    time: existingTime,
    reportId: fetchedReportId,
    hasEntry,
  } = await fetchExistingReportStats({
    sessionId,
    organizationId,
    userId,
    token,
    gameMode,
  });

  const reportPoints = hasEntry ? existingPoints + matchPoints : matchPoints;
  const reportTime = hasEntry
    ? mergeDurationStrings(existingTime, matchTimeSeconds)
    : toDurationString(matchTimeSeconds);
  const reportIdForPayload =
    hasEntry && fetchedReportId ? fetchedReportId : undefined;

  const reportPayload = {
    sessionId,
    organizationId,
    userId,
    gameId,
    name,
    role,
    token,
    gameMode,
    points: reportPoints,
    time: reportTime,
    ...(reportIdForPayload ? { reportId: reportIdForPayload } : {}),
  };
  const reportResult = await sendReport(reportPayload);
  const reportId = extractReportId(reportResult) ?? reportIdForPayload;

  return { reportId, reportPoints, hasEntry };
}

/** Extramile report add/update for one user using post-update stat_ini values. */
async function sendReportForScoreUser({
  sessionId,
  organizationId,
  userId: targetUserId,
  token,
  gameId,
  name,
  role,
  points,
  time,
  reportId,
  gameMode = 'pvp',
}) {
  const reportPayload = {
    sessionId,
    organizationId,
    userId: targetUserId,
    gameId,
    name,
    role,
    token,
    points,
    time,
    gameMode,
    ...(reportId ? { reportId } : {}),
  };
  console.log("reportPayload--------", reportPayload);

  const reportResult = await sendReport(reportPayload);
  const savedReportId = extractReportId(reportResult) ?? reportId;
  return { reportId: savedReportId, reportResult };
}

/**
 * One sendReport per unique userId (before updateScore).
 * Returns reportId from Extramile API per user for stat_ini storage.
 */
async function sendLobbyReportsForScoreUsers({
  sessionId,
  organizationId,
  token,
  gameId,
  role,
  scoreUsers,
  matchTimeSeconds,
  localUserId,
  localExistingReportId,
  gameMode = 'pvp',
}) {
  const seen = new Set();
  const reportIdsByUserId = {};
  const uniqueUsers = [];

  for (const u of scoreUsers) {
    const targetUserId = String(u?.userId ?? '').trim();
    if (!targetUserId || seen.has(targetUserId)) continue;
    seen.add(targetUserId);
    uniqueUsers.push(u);
  }

  await Promise.all(
    uniqueUsers.map(async (u) => {
      const targetUserId = String(u?.userId ?? '').trim();
      const isSelf = targetUserId === localUserId;
      let userAuthRole = resolveAuthRole(
        u.authRole ?? (isSelf ? role : '')
      );
      let userToken =
        String(u?.token ?? '').trim() ||
        (isSelf && userAuthRole !== 'GUEST_USER'
          ? String(token ?? '').trim()
          : '');
      // Guests have no JWT; opponents with empty token use guest score API.
      if (!isSelf && userAuthRole === 'ORG_USER' && !userToken) {
        userAuthRole = 'GUEST_USER';
      }
      if (userAuthRole !== 'GUEST_USER' && !userToken) {
        console.warn(`sendReport skipped: missing token for user ${targetUserId}`);
        return;
      }
      const displayName = resolvePlayerDisplayName(u);
      const matchPoints = Number(u?.score) || 0;

      const {
        points: existingPoints,
        time: existingTime,
        reportId: fetchedReportId,
        hasEntry,
      } = await fetchExistingReportStats({
        sessionId,
        organizationId,
        userId: targetUserId,
        token: userToken,
        gameMode,
      });

      const reportIdForPayload = hasEntry
        ? (isSelf && localExistingReportId) || fetchedReportId || undefined
        : undefined;
      const reportPoints = hasEntry ? existingPoints + matchPoints : matchPoints;
      const reportTime = hasEntry
        ? mergeDurationStrings(existingTime, matchTimeSeconds)
        : toDurationString(matchTimeSeconds);

      try {
        const { reportId } = await sendReportForScoreUser({
          sessionId,
          organizationId,
          userId: targetUserId,
          token: userToken,
          gameId,
          name: displayName,
          role: userAuthRole,
          points: reportPoints,
          time: reportTime,
          reportId: reportIdForPayload,
          gameMode,
        });
        if (reportId) {
          reportIdsByUserId[targetUserId] = reportId;
        }
      } catch (err) {
        console.error(`sendReport failed for user ${targetUserId}:`, err);
      }
    })
  );

  return {
    reportIdsByUserId,
    savedLocalReportId:
      reportIdsByUserId[localUserId] || localExistingReportId,
  };
}

/** Points for Extramile report + local DB update — matches logged-in player's outcome where applicable. */
function computePointsForGame({ gameMode, playerRole, winner, isDraw }) {
  if (isDraw) return 0;
  if (!winner) return 0;
  if (gameMode === 'offline') return 1;
  if (winner === playerRole) return 1;
  if (gameMode === 'ai') return 0;
  return 1;
}

/** Attach Extramile reportIds from sendReport onto updateScore user rows. */
function attachReportIdsToScoreUsers(scoreUsers, reportIdsByUserId) {
  if (!reportIdsByUserId || typeof reportIdsByUserId !== 'object') {
    return scoreUsers;
  }
  return scoreUsers.map((u) => {
    const uid = String(u?.userId ?? '').trim();
    const rid = reportIdsByUserId[uid];
    if (!rid || String(rid).trim() === '') return u;
    return { ...u, reportId: String(rid).trim() };
  });
}

function persistReportIdInUserData(userData, reportId, setMergedUserData) {
  if (!reportId || String(reportId).trim() === '') return;
  const rid = String(reportId).trim();
  const merged = { ...userData, reportId: rid };
  try {
    localStorage.setItem('userData', JSON.stringify(merged));
  } catch (_) {
    /* ignore */
  }
  setMergedUserData(merged);
}

/** Lobby PVP: winner 1, loser 0, draw 0 for both. */
function computePvpScoreForRole(role, winner, isDraw) {
  if (isDraw || !winner) return 0;
  return winner === role ? 1 : 0;
}

function resolveUpdateScoreMeta({
  lobbyContext,
  joinMeta,
  authName,
  authEmail,
  authLobby,
  authLink,
  authLoginPin,
  fallbackLobbyName,
}) {
  const join = joinMeta || {};
  const lobby =
    String(join.lobby ?? fallbackLobbyName ?? authLobby ?? lobbyContext.lobbyName ?? '')
      .trim() ||
    String(fallbackLobbyName ?? authLobby ?? lobbyContext.lobbyName ?? '').trim();
  const link = String(join.link ?? authLink ?? lobbyContext.link ?? '').trim();
  const rawPin = join.loginPin ?? authLoginPin ?? lobbyContext.loginPin;
  let loginPin = null;
  if (rawPin != null && rawPin !== '') {
    const n = Number(rawPin);
    if (Number.isFinite(n)) loginPin = Math.trunc(n);
  }
  if (loginPin == null && lobby) {
    const fromLobby = Number.parseInt(lobby, 10);
    if (Number.isFinite(fromLobby)) loginPin = fromLobby;
  }
  return {
    lobby,
    link,
    login_pin: loginPin,
    name: String(authName ?? '').trim(),
    email: String(authEmail ?? '').trim(),
  };
}

function resolveModeFromStorage() {
  const mode = readStoredCategoryMode();
  return mode || null;
}

function mapModeToSlice(mode) {
  if (mode === 'pvp') return 'pvp';
  if (mode === 'ai') return 'ai';
  if (mode === 'Pass & Play') return 'offline';
  if (mode === 'online') return 'online';
  return mode;
}

function Game() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const categoryMode = useMemo(
    () => resolveModeFromStorage(),
    [location.key]
  );
  const passPlayNames = useMemo(() => {
    return categoryMode === 'Pass & Play' ? readPassPlayPlayers() : null;
  }, [categoryMode]);
  const [lobbyContext] = useState(() => readLobbyContextFromStorage());
  const isLobbyPvp = categoryMode === 'pvp';
  const isPassPlay = categoryMode === 'Pass & Play';
  const [onlineView, setOnlineView] = useState('menu');
  const [joinCode, setJoinCode] = useState('');
  const [showRules, setShowRules] = useState(false);
  const leaderboardNavigateTimerRef = useRef(null);
  const leaderboardNavigatedSigRef = useRef(null);
  const playAgainResetActiveRef = useRef(peekLobbyRematchPending());
  const gameOverUiAtRef = useRef(0);

  const isPlayAgainResetActive = useCallback(() => {
    return peekLobbyRematchPending() || playAgainResetActiveRef.current;
  }, []);
  const initializedRef = useRef(false);
  const lastPushedHistoryLen = useRef(0);
  const lastRemoteHistoryLen = useRef(0);
  const lastAppliedRemoteSigRef = useRef('');
  const lastPushedSigRef = useRef('');
  const lastAppliedRematchSeqRef = useRef(0);
  const pvpBootstrappedRef = useRef(false);
  const pvpResolvedRoleRef = useRef(null);
  const onRemoteUpdateRef = useRef(null);
  const onLobbyDocUpdateRef = useRef(null);
  const lobbyStatusRef = useRef('');

  const gameState = useSelector(selectGameState);
  const gameMode = useSelector(selectGameMode);
  const difficulty = useSelector(selectDifficulty);
  const playerRole = useSelector(selectPlayerRole);
  const settings = useSelector(selectSettings);
  const isAIThinking = useSelector(selectIsAIThinking);
  const showTurnTransition = useSelector(selectShowTurnTransition);
  const error = useSelector(selectError);
  const canMakeMove = useSelector(selectCanMakeMove);
  const themeData = useSelector((state) => state.theme?.data);

  console.log("gameMode--------", gameMode);  

  const themeRules = useMemo(() => {
    const raw = themeData?.rules;
    if (!Array.isArray(raw)) return null;
    const items = raw.map((rule) => String(rule ?? '').trim()).filter(Boolean);
    return items.length > 0 ? items : null;
  }, [themeData?.rules]);

  const rulesTitle = useMemo(() => {
    const title = String(themeData?.main_title ?? '').trim();
    return title || 'How to Play';
  }, [themeData?.main_title]);

  const themeColors = useMemo(
    () => themeData?.colors || themeData || {},
    [themeData],
  );

  const rulesTextStyle = themeColors?.text_color
    ? { color: themeColors.text_color }
    : undefined;

  const { playPlace, playShift, playWin, playDraw, playInvalid } = useSound();

  const pvpSettings = { allowDiagonal: true, maxPieces: 4 };

  const pushPvpStateToFirestore = useCallback((stateOverride) => {
    if (!isLobbyPvp) return;
    const next = stateOverride ?? store.getState().shiftGame?.gameState;
    if (!next) return;
    const sig = gameStateSignature(next);
    if (sig === lastPushedSigRef.current) return;
    const moveLen = next.moveHistory?.length ?? 0;
    lobbyPvpRef.current?.pushGameState(next).then((ok) => {
      if (!ok) return;
      lastPushedSigRef.current = sig;
      lastPushedHistoryLen.current = moveLen;
    });
  }, [isLobbyPvp]);

  const lobbyPvpRef = useRef(null);
  const sessionIdFromSession = useSelector(selectSessionId);
  const organizationIdFromSession = useSelector(selectOrganizationId);
  const gameClockStartRef = useRef(null);
  const gameOverSubmittedSigRef = useRef(null);
  const gameOverSubmitInFlightRef = useRef(null);

  const persistScoreboard = useCallback((payload) => {
    writeScoreboardData({
      categoryMode: categoryMode || resolveModeFromStorage(),
      isLobbyPvp,
      isPassPlay,
      scoreUpdatedAt: Date.now(),
      ...payload,
    });
  }, [categoryMode, isLobbyPvp, isPassPlay]);
  const lobbyJoinMetaRef = useRef({
    lobby: '',
    link: '',
    loginPin: null,
  });
  /** Merged userData after fetchReport (e.g. reportId); persisted to localStorage. */
  const [mergedUserData, setMergedUserData] = useState(null);
  const mergedUserDataRef = useRef(null);
  const [hudLocalIdentity, setHudLocalIdentity] = useState({
    name: 'Player',
    character: null,
  });
  const [hudLobbyIdentities, setHudLobbyIdentities] = useState({
    x: null,
    o: null,
  });
  useEffect(() => {
    mergedUserDataRef.current = mergedUserData;
  }, [mergedUserData]);

  const applyRemotePvpState = useCallback(
    (remote, meta) => {
      if (!isLobbyPvp || !remote) return;

      const remoteIsFinished = Boolean(remote.winner || remote.isDraw);
      const incomingRematchSeq = Number(meta?.rematchSeq) || 0;

      if (isPlayAgainResetActive() && remoteIsFinished) {
        return;
      }

      const remoteSig = gameStateSignature(remote);
      const slice = store.getState().shiftGame;
      const localSig = slice.gameState ? gameStateSignature(slice.gameState) : '';
      const localLen = slice.gameState?.moveHistory?.length ?? 0;
      const remoteLen = remote.moveHistory?.length ?? 0;

      if (remoteSig === localSig) {
        lastAppliedRemoteSigRef.current = remoteSig;
        return;
      }

      const rematchSeq = incomingRematchSeq;
      const isRematchBySeq = rematchSeq > lastAppliedRematchSeqRef.current;
      const localOver = Boolean(slice.gameState?.winner || slice.gameState?.isDraw);
      const isRematchReset =
        isRematchBySeq ||
        (localOver &&
          remoteLen === 0 &&
          !remote.winner &&
          !remote.isDraw);

      if (slice.gameState && remoteLen < localLen && !isRematchReset) {
        return;
      }

      const fromSelf =
        meta?.updatedBy && String(meta.updatedBy) === String(lobbyContext.userId);
      if (fromSelf && remoteLen <= localLen) {
        if (remoteSig === lastPushedSigRef.current) {
          lastAppliedRemoteSigRef.current = remoteSig;
          return;
        }
      }

      const role = pvpResolvedRoleRef.current || slice.playerRole || 'X';

      dispatch(setLobbyPvpSession({ playerRole: role }));

      if (!slice.gameState) {
        dispatch(
          startGame({
            mode: 'pvp',
            difficulty: 'medium',
            playerRole: role,
            settings: pvpSettings,
          })
        );
      }

      dispatch(syncGameState(remote));
      lastAppliedRemoteSigRef.current = remoteSig;
      lastPushedSigRef.current = remoteSig;
      if (rematchSeq > 0) {
        lastAppliedRematchSeqRef.current = rematchSeq;
      }
      if (isRematchReset) {
        gameOverSubmittedSigRef.current = null;
        gameOverSubmitInFlightRef.current = null;
        gameClockStartRef.current = Date.now();
        leaderboardNavigatedSigRef.current = null;
        clearLeaderboardNavigatedFlag();
      }
    },
    [isLobbyPvp, lobbyContext.userId, dispatch, isPlayAgainResetActive]
  );

  useEffect(() => {
    onRemoteUpdateRef.current = applyRemotePvpState;
  }, [applyRemotePvpState]);

  const navigateToLeaderboardIfReady = useCallback(
    (sig, docMeta) => {
      if (!isLobbyPvp || !sig || isPlayAgainResetActive()) return;
      if (leaderboardNavigatedSigRef.current === sig) return;

      const ready =
        docMeta &&
        isLobbyGameScoresReadyFromDoc(
          {
            shiftGameScoresReady: docMeta.shiftGameScoresReady,
            shiftGameLastMatchSig: docMeta.shiftGameLastMatchSig,
            status: docMeta.status,
            shiftGamePlayerStats: docMeta.shiftGamePlayerStats,
          },
          sig
        );

      if (!ready) return;

      leaderboardNavigatedSigRef.current = sig;
      gameOverSubmittedSigRef.current = sig;
      navigate('/leaderboard', { replace: true });
    },
    [isLobbyPvp, navigate, isPlayAgainResetActive]
  );

  const applyLobbyDocStatus = useCallback(
    (meta) => {
      if (!isLobbyPvp) return;
      const nextStatus = String(meta?.status ?? '').trim().toLowerCase();
      lobbyStatusRef.current = nextStatus;

      const live = store.getState().shiftGame?.gameState;
      const isOver = Boolean(live?.winner || live?.isDraw);
      if (!isOver || isPlayAgainResetActive()) return;

      const sig = `${live.winner ?? ''}-${live.isDraw ? '1' : '0'}-${live.moveHistory?.length ?? 0}`;
      navigateToLeaderboardIfReady(sig, meta);
    },
    [isLobbyPvp, isPlayAgainResetActive, navigateToLeaderboardIfReady]
  );

  useEffect(() => {
    onLobbyDocUpdateRef.current = applyLobbyDocStatus;
  }, [applyLobbyDocStatus]);

  const lobbyPvp = useLobbyPvpFirestore({
    enabled: isLobbyPvp,
    sessionId: lobbyContext.sessionId,
    lobbyName: lobbyContext.lobbyName,
    userId: lobbyContext.userId,
    onRemoteUpdateRef,
    onLobbyDocUpdateRef,
  });

  lobbyPvpRef.current = lobbyPvp;
  pvpResolvedRoleRef.current = lobbyPvp.resolvedRole;

  const multiplayer = useMultiplayer();
  const {
    roomCode,
    opponentJoined,
    gameState: remoteGameState,
    playerRole: onlineRole,
    connectionStatus,
    error: mpError,
    createRoom,
    joinRoom,
    updateGameState,
    leaveRoom,
  } = multiplayer;

  useEffect(() => {
    let cancelled = false;
    const ud = lobbyContext.userData || {};
    const userId = String(lobbyContext.userId ?? '').trim();
    const token = String(ud.token ?? '').trim();

    (async () => {
      try {
        const session = store.getState().session;
        const sessionId = String(ud.sessionId ?? session.sessionId ?? '').trim();
        const organizationId = String(ud.organizationId ?? session.organizationId ?? '').trim();
        if (!userId || !sessionId || !organizationId || !token) return;

        const loadGameMode =
          store.getState().shiftGame?.gameMode ||
          sessionStorage.getItem('gameCategoryMode') ||
          'pvp';
        const data = await fetchReportApi({
          sessionId,
          organizationId,
          userId,
          token,
          gameMode: loadGameMode,
        });
        if (cancelled || !data?.success || !data.report) return;
        const rid =
          data.report.reportId ??
          data.report.report_id ??
          data.report.id;
        if (rid == null || String(rid).trim() === '') return;
        const merged = { ...ud, reportId: rid };
        try {
          localStorage.setItem('userData', JSON.stringify(merged));
        } catch (_) {
          /* ignore */
        }
        if (!cancelled) setMergedUserData(merged);
      } catch (e) {
        console.warn('fetchReport on game load:', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.key]);

  useEffect(() => {
    if (isPassPlay && !passPlayNames) {
      navigate('/passplay/create', { replace: true });
    }
  }, [isPassPlay, passPlayNames, navigate]);

  useEffect(() => {
    if (!isLobbyPvp) return undefined;

    let cancelled = false;
    const lobbyName =
      String(location.state?.lobbyName ?? '').trim() ||
      String(lobbyContext.lobbyName ?? '').trim();
    const linkFromState = String(location.state?.link ?? '').trim();
    const pinFromState =
      location.state?.loginPin ?? location.state?.pin ?? null;

    const base = {
      lobby: lobbyName,
      link: linkFromState || String(lobbyContext.link ?? '').trim(),
      loginPin:
        pinFromState != null && pinFromState !== ''
          ? Math.trunc(Number(pinFromState))
          : lobbyContext.loginPin ?? null,
    };

    if (!Number.isFinite(base.loginPin)) {
      base.loginPin = null;
    }

    lobbyJoinMetaRef.current = base;

    (async () => {
      const sessionId = String(lobbyContext.sessionId ?? '').trim();
      if (!sessionId || !lobbyName) return;

      const remote = await fetchLobbyJoinMeta(sessionId, lobbyName);
      if (cancelled) return;

      lobbyJoinMetaRef.current = {
        lobby: remote.lobby || base.lobby,
        link: remote.link || base.link,
        loginPin:
          remote.loginPin != null ? remote.loginPin : base.loginPin,
      };

      try {
        if (lobbyJoinMetaRef.current.link) {
          sessionStorage.setItem(
            'mythFactLobbyLink',
            lobbyJoinMetaRef.current.link
          );
        }
        if (lobbyJoinMetaRef.current.loginPin != null) {
          sessionStorage.setItem(
            'mythFactLobbyLoginPin',
            String(lobbyJoinMetaRef.current.loginPin)
          );
        }
      } catch {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isLobbyPvp,
    location.state,
    lobbyContext.sessionId,
    lobbyContext.lobbyName,
    lobbyContext.link,
    lobbyContext.loginPin,
  ]);

  useEffect(() => {
    dispatch(setBackButtonUrl(isLobbyPvp ? '/waiting' : '/category'));
    const html = document.documentElement;
    const body = document.body;
    html.classList.add('game-page-bg');
    body.classList.add('game-page-bg');
    return () => {
      html.classList.remove('game-page-bg');
      body.classList.remove('game-page-bg');
    };
  }, [dispatch, isLobbyPvp]);

  useEffect(() => {
    try {
      const hasPendingLocalReset =
        sessionStorage.getItem('pendingLocalGameReset') === '1';
      const hasPendingLobbyRematch = peekLobbyRematchPending();
      if (hasPendingLocalReset || hasPendingLobbyRematch) {
        initializedRef.current = false;
      }
    } catch {
      /* ignore */
    }
  }, [location.key]);

  useEffect(() => {
    initializedRef.current = false;
    pvpBootstrappedRef.current = false;
    pvpResolvedRoleRef.current = null;
    lastPushedSigRef.current = '';
    lastAppliedRemoteSigRef.current = '';
    lastPushedHistoryLen.current = 0;
    lastRemoteHistoryLen.current = 0;
    gameOverSubmittedSigRef.current = null;
    gameOverSubmitInFlightRef.current = null;
    leaderboardNavigatedSigRef.current = null;
    gameClockStartRef.current = null;
  }, [categoryMode]);

  useEffect(() => {
    if (peekPendingLocalGameReset()) {
      initializedRef.current = false;
    }
  }, [location.key]);

  useEffect(() => {
    if (initializedRef.current || !categoryMode) return;

    if (categoryMode === 'online') {
      initializedRef.current = true;
      return;
    }

    if (categoryMode === 'pvp') {
      initializedRef.current = true;
      return;
    }

    const preparedSig = readScoreboardMatchSig();
    if (isScoreboardGamePrepared(preparedSig)) {
      const existing = store.getState().shiftGame?.gameState;
      const finished = Boolean(existing?.winner || existing?.isDraw);
      if (existing && !finished) {
        initializedRef.current = true;
        return;
      }
    }

    const sliceMode = mapModeToSlice(categoryMode);
    const defaultSettings = { allowDiagonal: true, maxPieces: 4 };
    const pendingStarter = consumePendingLocalGameReset();
    if (pendingStarter) {
      dispatch(
        startGame({
          mode: sliceMode,
          difficulty: 'medium',
          playerRole: 'X',
          startingPlayer: pendingStarter,
          settings: defaultSettings,
        })
      );
      initializedRef.current = true;
      return;
    }

    const saved = loadLocalGameSnapshot(categoryMode);

    if (saved?.gameState) {
      dispatch(
        startGame({
          mode: sliceMode,
          difficulty: saved.difficulty || 'medium',
          playerRole: saved.playerRole || 'X',
          settings: saved.settings || defaultSettings,
        })
      );
      dispatch(syncGameState(saved.gameState));
      if ((saved.gameState.moveHistory?.length ?? 0) > 0) {
        dispatch(dismissTurnTransition());
      }
    } else {
      const startingPlayer = getNextStartingPlayer(
        starterStorageKey(categoryMode)
      );
      dispatch(
        startGame({
          mode: sliceMode,
          difficulty: 'medium',
          playerRole: 'X',
          startingPlayer,
          settings: defaultSettings,
        })
      );
    }
    initializedRef.current = true;
  }, [categoryMode, dispatch]);

  useEffect(() => {
    if (!isLobbyPvp || !lobbyPvp.ready || !lobbyPvp.resolvedRole) return;
    if (pvpBootstrappedRef.current) return;
    if (peekLobbyRematchPending()) return;

    pvpBootstrappedRef.current = true;
    pvpResolvedRoleRef.current = lobbyPvp.resolvedRole;
    lastPushedSigRef.current = '';
    lastAppliedRemoteSigRef.current = '';
    lastPushedHistoryLen.current = 0;
    gameOverSubmittedSigRef.current = null;
    gameOverSubmitInFlightRef.current = null;
    leaderboardNavigatedSigRef.current = null;

    dispatch(setLobbyPvpSession({ playerRole: lobbyPvp.resolvedRole }));

    const existingState = store.getState().shiftGame?.gameState;
    const hasFinishedBoard = Boolean(
      existingState?.winner || existingState?.isDraw
    );

    if (!existingState || hasFinishedBoard) {
      dispatch(
        startGame({
          mode: 'pvp',
          difficulty: 'medium',
          playerRole: lobbyPvp.resolvedRole,
          settings: pvpSettings,
        })
      );
    }

    void lobbyPvp.pullLatestGameState();
    if (!existingState) {
      void lobbyPvp.ensureInitialRemoteState(pvpSettings);
    }

    if (!hasFinishedBoard && lobbyContext.sessionId && lobbyContext.lobbyName) {
      void setLobbyGameStatus(
        lobbyContext.sessionId,
        lobbyContext.lobbyName,
        'play'
      );
      lobbyStatusRef.current = 'play';
    }
  }, [
    isLobbyPvp,
    lobbyPvp.ready,
    lobbyPvp.resolvedRole,
    lobbyPvp.ensureInitialRemoteState,
    lobbyPvp.pullLatestGameState,
    dispatch,
    lobbyContext.sessionId,
    lobbyContext.lobbyName,
  ]);

  useEffect(() => {
    if (!isLobbyPvp || !lobbyPvp.ready) return;
    if (!peekLobbyRematchPending()) return;

    const stale = store.getState().shiftGame?.gameState;
    if (stale?.winner || stale?.isDraw) {
      dispatch(exitGame());
    }

    playAgainResetActiveRef.current = true;
    pvpBootstrappedRef.current = false;
    lastAppliedRematchSeqRef.current = 0;
    lastAppliedRemoteSigRef.current = '';
    lastPushedSigRef.current = '';
    lastPushedHistoryLen.current = 0;
    gameOverSubmittedSigRef.current = null;
    gameOverSubmitInFlightRef.current = null;
    gameClockStartRef.current = Date.now();
    leaderboardNavigatedSigRef.current = null;
    clearLeaderboardNavigatedFlag();
    gameOverUiAtRef.current = 0;
    if (leaderboardNavigateTimerRef.current) {
      clearTimeout(leaderboardNavigateTimerRef.current);
      leaderboardNavigateTimerRef.current = null;
    }

    const role = pvpResolvedRoleRef.current || lobbyPvp.resolvedRole || 'X';
    dispatch(
      startGame({
        mode: 'pvp',
        difficulty: 'medium',
        playerRole: role,
        settings: pvpSettings,
      })
    );
    consumeLobbyRematchPending();
    lobbyStatusRef.current = 'play';

    void (async () => {
      await lobbyPvp.pullLatestGameState();
      pvpBootstrappedRef.current = true;
      playAgainResetActiveRef.current = false;
    })();
  }, [
    isLobbyPvp,
    lobbyPvp.ready,
    lobbyPvp.resolvedRole,
    lobbyPvp.pullLatestGameState,
    dispatch,
    location.key,
  ]);

  /** Push completed moves to Firestore (backup if click-handler push was skipped). */
  useEffect(() => {
    if (!isLobbyPvp || !gameState || !playerRole) return;
    const len = gameState.moveHistory?.length ?? 0;
    if (len === 0 || len <= lastPushedHistoryLen.current) return;
    const lastMove = gameState.moveHistory[len - 1];
    if (lastMove?.player !== playerRole) return;
    pushPvpStateToFirestore(gameState);
  }, [gameState, isLobbyPvp, playerRole, pushPvpStateToFirestore]);

  useEffect(() => {
    if (!isLocalPersistedCategory(categoryMode) || !gameState) return;
    saveLocalGameSnapshot(categoryMode, {
      gameState,
      gameMode,
      difficulty,
      playerRole,
      settings,
    });
  }, [categoryMode, gameState, gameMode, difficulty, playerRole, settings]);

  useEffect(() => {
    if (categoryMode !== 'online' || !opponentJoined || !remoteGameState) return;

    const remoteLen = remoteGameState.moveHistory?.length ?? 0;
    if (remoteLen === lastRemoteHistoryLen.current && gameState) return;

    if (!gameState) {
      dispatch(
        startGame({
          mode: 'online',
          playerRole: onlineRole,
          settings: { allowDiagonal: true, maxPieces: 4 },
        })
      );
    }
    dispatch(syncGameState(remoteGameState));
    lastRemoteHistoryLen.current = remoteLen;
    lastPushedHistoryLen.current = remoteLen;
  }, [categoryMode, opponentJoined, remoteGameState, onlineRole, gameState, dispatch]);

  useEffect(() => {
    if (gameMode !== 'online' || !gameState || !roomCode || !opponentJoined) return;
    const len = gameState.moveHistory?.length ?? 0;
    if (len <= lastPushedHistoryLen.current) return;
    const lastMove = gameState.moveHistory[len - 1];
    if (lastMove?.player === playerRole) {
      updateGameState(gameState);
      lastPushedHistoryLen.current = len;
    }
  }, [gameState, gameMode, roomCode, opponentJoined, playerRole, updateGameState]);

  useEffect(() => {
    if (!gameState) return;
    const over = Boolean(gameState.winner || gameState.isDraw);
    if (over) return;
    const len = gameState.moveHistory?.length ?? 0;
    if (len === 0) {
      gameClockStartRef.current = Date.now();
      gameOverSubmittedSigRef.current = null;
    } else if (gameClockStartRef.current == null) {
      gameClockStartRef.current = Date.now();
    }
  }, [gameState, gameState?.winner, gameState?.isDraw, gameState?.moveHistory?.length]);

  const getGameReportAuthContext = useCallback(() => {
    const lobby = readLobbyContextFromStorage();
    const merged = mergedUserDataRef.current;
    const session = store.getState().session;
    const authUser = store.getState().auth?.user;
    const storedUserData = lobby.userData || {};
    const userData = {
      ...storedUserData,
      ...(authUser || {}),
      ...(merged || {}),
    };
    const userId = String(
      userData.userId || userData.userid || userData.id || lobby.userId || ''
    ).trim();
    const sessionId = String(
      userData.sessionId || session.sessionId || lobby.sessionId || ''
    ).trim();
    const organizationId = String(
      userData.organizationId || session.organizationId || ''
    ).trim();
    const token = String(
      (merged || {}).token ?? storedUserData.token ?? ''
    ).trim();
    const role =
      userData.role ?? userData.roles ?? userData.source ?? 'ORG_USER';
    const existingReportIdRaw = userData.reportId ?? userData.report_id;
    const existingReportId =
      existingReportIdRaw != null && String(existingReportIdRaw).trim() !== ''
        ? String(existingReportIdRaw).trim()
        : undefined;
    const email = String(userData.email ?? '').trim();
    const lobbyFromUser = String(userData.lobby ?? '').trim();
    const lobbyFromStorage = String(lobby.lobbyName ?? '').trim();
    const lobbyValue = lobbyFromStorage || lobbyFromUser;
    const rawPin =
      userData.loginPin ?? userData.login_pin ?? userData.pin ?? lobby.loginPin;
    let loginPin = null;
    if (rawPin != null && rawPin !== '') {
      const n = Number(rawPin);
      if (Number.isFinite(n)) loginPin = Math.trunc(n);
    }
    if (loginPin == null && lobbyValue) {
      const fromLobby = Number.parseInt(lobbyValue, 10);
      if (Number.isFinite(fromLobby)) loginPin = fromLobby;
    }
    return {
      userId,
      sessionId,
      organizationId,
      token,
      userData,
      role,
      gameId: userData.gameId ?? null,
      name: resolvePlayerDisplayName(userData),
      email,
      lobby: lobbyValue,
      link: String(userData.link ?? userData.joinLink ?? lobby.link ?? '').trim(),
      loginPin,
      existingReportId,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { userId, sessionId, name } = getGameReportAuthContext();
        if (!cancelled) {
          setHudLocalIdentity((prev) => ({
            ...prev,
            name: String(name ?? '').trim() || 'Player',
          }));
        }
        if (!sessionId || !userId) return;

        const sessionUserRef = doc(
          database,
          `sessions/${sessionId}/users/${userId}`
        );
        const snap = await getDoc(sessionUserRef);
        if (!snap.exists() || cancelled) return;
        const sessionUserData = snap.data() || {};
        const character = parseHudCharacter(sessionUserData.character);
        if (!cancelled) {
          setHudLocalIdentity((prev) => ({
            ...prev,
            name: resolvePlayerDisplayName(sessionUserData),
            character,
          }));
        }
      } catch (err) {
        console.warn('HUD character fetch failed:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getGameReportAuthContext, location.key]);

  useEffect(() => {
    if (!isLobbyPvp) return undefined;
    const sessionId = String(lobbyContext.sessionId ?? '').trim();
    const lobbyName = String(lobbyContext.lobbyName ?? '').trim();
    if (!sessionId || !lobbyName) return undefined;

    let cancelled = false;

    const load = async () => {
      try {
        const identities = await fetchLobbyHudIdentities(sessionId, lobbyName);
        if (!cancelled) setHudLobbyIdentities(identities);
      } catch (err) {
        console.warn('Lobby HUD identities fetch failed:', err);
      }
    };

    void load();
    const interval = setInterval(load, 8000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isLobbyPvp, lobbyContext.sessionId, lobbyContext.lobbyName, location.key]);

  const submitGameOverResults = useCallback(async () => {
    const state = store.getState().shiftGame?.gameState;
    if (!state) return { ok: false };

    const winner = state.winner;
    const isDraw = state.isDraw;
    if (!winner && !isDraw) return { ok: false };

    const sig = `${winner ?? ''}-${isDraw ? '1' : '0'}-${state.moveHistory?.length ?? 0}`;
    if (gameOverSubmittedSigRef.current === sig) return { ok: true, sig };
    if (gameOverSubmitInFlightRef.current === sig) return { ok: false, sig };
    gameOverSubmitInFlightRef.current = sig;

    const persistMatchScoreboard = (payload) =>
      persistScoreboard({ endedMatchSig: sig, ...payload });

    const {
      userId,
      sessionId,
      organizationId,
      token,
      userData,
      role,
      gameId,
      name,
      email,
      lobby: authLobby,
      link: authLink,
      loginPin: authLoginPin,
      existingReportId,
    } = getGameReportAuthContext();

    const started = gameClockStartRef.current ?? Date.now();
    const timeSeconds = Math.max(0, Math.floor((Date.now() - started) / 1000));
    const sliceMode = store.getState().shiftGame?.gameMode;
    const slicePlayerRole = store.getState().shiftGame?.playerRole;

    try {
      if (isPassPlay) {
        if (!passPlayNames) {
          console.warn('Pass & Play result save skipped: player names missing');
          return { ok: false, sig };
        }
        const winnerName = winnerLabel({ winner, isDraw, players: passPlayNames });
        const { player1points, player2points } = computePassPlayPoints({ winner, isDraw });
        persistMatchScoreboard({
          players: [
            { name: passPlayNames.xName, score: player1points },
            { name: passPlayNames.oName, score: player2points },
          ],
        });
        if (userId && sessionId && organizationId) {
          await savePassPlayResult({
            sessionId,
            organizationId,
            userId,
            token,
            playerXName: passPlayNames.xName,
            playerOName: passPlayNames.oName,
            winner: winnerName,
            player1points,
            player2points,
            totalTime: timeSeconds,
          });
        }
        gameOverSubmittedSigRef.current = sig;
        return { ok: true, sig };
      }

      if (!userId || !sessionId || !organizationId) {
        console.warn('Game over API skipped: missing session credentials', {
          hasUserId: Boolean(userId),
          hasSessionId: Boolean(sessionId),
          hasOrganizationId: Boolean(organizationId),
        });
        return { ok: false, sig };
      }

      if (sliceMode === 'ai') {
        const aiRole = slicePlayerRole === 'X' ? 'O' : 'X';
        const playerPoints = computePvpScoreForRole(
          slicePlayerRole,
          winner,
          isDraw
        );
        const opponentPoints = computePvpScoreForRole(aiRole, winner, isDraw);
        const playerDisplayName = resolvePlayerDisplayName(userData);
        const opponentDisplayName = 'Computer';
        const winnerText = isDraw
          ? 'Draw'
          : winner === slicePlayerRole
            ? playerDisplayName
            : opponentDisplayName;

        const matchPoints = computePointsForGame({
          gameMode: sliceMode,
          playerRole: slicePlayerRole,
          winner,
          isDraw,
        });

        const { reportId, reportPoints } = await fetchMergeAndSendReport({
          sessionId,
          organizationId,
          userId,
          token,
          gameId,
          name,
          role,
          gameMode: 'ai',
          matchPoints,
          matchTimeSeconds: timeSeconds,
        });

        await saveComputervsPersonResult({
          sessionId,
          organizationId,
          userId,
          token,
          playerName: playerDisplayName,
          opponentName: opponentDisplayName,
          winner: winnerText,
          playerPoints,
          opponentPoints,
          totalTime: timeSeconds,
          ...(reportId ? { reportId } : {}),
        });

        persistReportIdInUserData(userData, reportId, setMergedUserData);

        persistMatchScoreboard({
          players: [
            {
              userId,
              name: playerDisplayName,
              email,
              score: reportPoints,
            },
            { name: opponentDisplayName, score: opponentPoints },
          ],
        });
        gameOverSubmittedSigRef.current = sig;
        return { ok: true, sig };
      }

      if (sliceMode !== 'pvp') return { ok: false, sig };

      const lobbyName = String(lobbyContext.lobbyName ?? '').trim();
      const isLobbyPvpMode = isPassPlay === false && (isLobbyPvp || sliceMode === 'pvp');

      if (isLobbyPvpMode && lobbyName) {
        const scoresReady = await waitForLobbyGameScoresReady(
          sessionId,
          lobbyName,
          sig,
          300
        );
        if (scoresReady) {
          gameOverSubmittedSigRef.current = sig;
          return { ok: true, sig };
        }

        const lobbyPlayers = await fetchLobbyLivePlayers(sessionId, lobbyName);

        const claimed = await claimLobbyGameOverScores(
          sessionId,
          lobbyName,
          sig,
          userId
        );

        if (!claimed) {
          const ready = await waitForLobbyGameScoresReady(
            sessionId,
            lobbyName,
            sig,
            LOBBY_SCORE_SUBMIT_WAIT_MS
          );
          if (!ready) {
            return { ok: false, sig };
          }
          gameOverSubmittedSigRef.current = sig;
          return { ok: true, sig };
        }

        const myPoints = computePvpScoreForRole(slicePlayerRole, winner, isDraw);

        const scoreMeta = resolveUpdateScoreMeta({
          lobbyContext,
          joinMeta: lobbyJoinMetaRef.current,
          authName: name,
          authEmail: email,
          authLobby,
          authLink,
          authLoginPin,
          fallbackLobbyName: lobbyName,
        });

        const scoreUsers =
          lobbyPlayers.length >= 2
            ? lobbyPlayers.map((p) => ({
                userId: String(p.userId ?? '').trim(),
                token:
                  String(p.userId) === userId
                    ? token
                    : String(p.token ?? '').trim(),
                name:
                  String(p.userId) === userId
                    ? resolvePlayerDisplayName(userData)
                    : resolvePlayerDisplayName({
                        name: p.name,
                        email: p.email,
                        firstName: p.firstName,
                        lastName: p.lastName,
                      }),
                email: String(p.email ?? '').trim() || scoreMeta.email,
                role: p.role,
                authRole:
                  String(p.userId) === userId
                    ? resolveAuthRole(role)
                    : resolveAuthRole(p.authRole),
                score: computePvpScoreForRole(p.role, winner, isDraw),
                wrongCount: 0,
                status: 'gameover',
                totalTime: timeSeconds,
                lobby: scoreMeta.lobby,
                link: scoreMeta.link,
                login_pin: scoreMeta.login_pin,
              }))
            : [
                {
                  userId,
                  token,
                  name: resolvePlayerDisplayName(userData),
                  email: scoreMeta.email || email,
                  role: slicePlayerRole,
                  authRole: resolveAuthRole(role),
                  score: myPoints,
                  wrongCount: 0,
                  status: 'gameover',
                  totalTime: timeSeconds,
                  lobby: scoreMeta.lobby,
                  link: scoreMeta.link,
                  login_pin: scoreMeta.login_pin,
                },
              ];

        const { reportIdsByUserId, savedLocalReportId } =
          await sendLobbyReportsForScoreUsers({
            sessionId,
            organizationId,
            token,
            gameId,
            role,
            scoreUsers,
            matchTimeSeconds: timeSeconds,
            localUserId: userId,
            localExistingReportId: existingReportId,
            gameMode: 'pvp',
          });

        const localReportId = savedLocalReportId || existingReportId;
        persistReportIdInUserData(userData, localReportId, setMergedUserData);

        const scoreUsersWithReportIds = attachReportIdsToScoreUsers(
          scoreUsers,
          reportIdsByUserId
        );

        const updateResult = await updateScore({
          sessionId,
          organizationId,
          users: scoreUsersWithReportIds,
          role,
          token,
          gameId,
          reportId: localReportId,
          lobby: scoreMeta.lobby,
          link: scoreMeta.link,
          login_pin: scoreMeta.login_pin,
          name: scoreMeta.name || name,
          email: scoreMeta.email || email,
        });

        const fsOk = await persistLobbyPvpScoresToFirestore({
          sessionId,
          lobbyName,
          scoreUsers: scoreUsersWithReportIds,
          updateResult,
          matchSig: sig,
          winner,
          isDraw,
          matchTimeSeconds: timeSeconds,
        });
        if (!fsOk) {
          return { ok: false, sig };
        }

        gameOverSubmittedSigRef.current = sig;
        lobbyStatusRef.current = 'gameover';
        return { ok: true, sig };
      }

      const points = computePointsForGame({
        gameMode: sliceMode,
        playerRole: slicePlayerRole,
        winner,
        isDraw,
      });

      const { reportId } = await fetchMergeAndSendReport({
        sessionId,
        organizationId,
        userId,
        token,
        gameId,
        name,
        role,
        gameMode: 'pvp',
        matchPoints: points,
        matchTimeSeconds: timeSeconds,
      });

      persistReportIdInUserData(userData, reportId, setMergedUserData);

      const scoreMeta = resolveUpdateScoreMeta({
        lobbyContext,
        joinMeta: lobbyJoinMetaRef.current,
        authName: name,
        authEmail: email,
        authLobby,
        authLink,
        authLoginPin,
        fallbackLobbyName: String(lobbyContext.lobbyName ?? '').trim(),
      });

      const scoreUsers = [
        {
          userId,
          name: resolvePlayerDisplayName(userData),
          email: scoreMeta.email || email,
          score: points,
          wrongCount: 0,
          status: 'gameover',
          totalTime: timeSeconds,
          lobby: scoreMeta.lobby,
          link: scoreMeta.link,
          login_pin: scoreMeta.login_pin,
          ...(reportId ? { reportId } : {}),
        },
      ];
      const updateResult = await updateScore({
        sessionId,
        organizationId,
        users: scoreUsers,
        role,
        token,
        gameId,
        reportId,
        lobby: scoreMeta.lobby,
        link: scoreMeta.link,
        login_pin: scoreMeta.login_pin,
        name: scoreMeta.name || name,
        email: scoreMeta.email || email,
      });
      persistMatchScoreboard({
        players: playersFromUpdateScoreResult(scoreUsers, updateResult),
      });
      gameOverSubmittedSigRef.current = sig;
      return { ok: true, sig };
    } catch (err) {
      console.error('Game over save failed:', err);
      return { ok: false, sig };
    } finally {
      if (gameOverSubmitInFlightRef.current === sig) {
        gameOverSubmitInFlightRef.current = null;
      }
    }
  }, [
    getGameReportAuthContext,
    isPassPlay,
    isLobbyPvp,
    lobbyContext.lobbyName,
    passPlayNames,
    persistScoreboard,
  ]);

  useEffect(() => {
    if (isPlayAgainResetActive()) return undefined;

    const live = store.getState().shiftGame?.gameState;
    const isOver = Boolean(live?.winner || live?.isDraw);

    if (!isOver) {
      gameOverUiAtRef.current = 0;
      leaderboardNavigatedSigRef.current = null;
      if (leaderboardNavigateTimerRef.current) {
        clearTimeout(leaderboardNavigateTimerRef.current);
        leaderboardNavigateTimerRef.current = null;
      }
      return undefined;
    }

    const sig = `${live.winner ?? ''}-${live.isDraw ? '1' : '0'}-${live.moveHistory?.length ?? 0}`;
    if (leaderboardNavigatedSigRef.current === sig) return undefined;

    if (!gameOverUiAtRef.current) {
      gameOverUiAtRef.current = Date.now();
    }

    leaderboardNavigateTimerRef.current = setTimeout(() => {
      leaderboardNavigateTimerRef.current = null;
      void (async () => {
        const result = await submitGameOverResults();
        if (isPlayAgainResetActive()) return;
        if (result?.ok) {
          leaderboardNavigatedSigRef.current = sig;
          navigate('/leaderboard', { replace: true });
        }
      })();
    }, RESULT_OVERLAY_DELAY_MS);

    return () => {
      if (leaderboardNavigateTimerRef.current) {
        clearTimeout(leaderboardNavigateTimerRef.current);
        leaderboardNavigateTimerRef.current = null;
      }
    };
  }, [
    gameState?.winner,
    gameState?.isDraw,
    gameState?.moveHistory?.length,
    submitGameOverResults,
    navigate,
    isPlayAgainResetActive,
  ]);

  const prevGameRef = useRef(null);
  useEffect(() => {
    if (!gameState) return;
    const prev = prevGameRef.current;
    if (prev) {
      const justWon = gameState.winner && !prev.winner;
      const justDrew = gameState.isDraw && !prev.isDraw;
      if (justWon) {
        playWin();
        fireGameOverConfetti();
      } else if (justDrew) {
        playDraw();
        fireGameOverConfetti();
      }
      else if (gameState.lastMove && gameState.lastMove !== prev.lastMove) {
        if (gameState.lastMove.type === 'place') playPlace();
        else if (gameState.lastMove.type === 'shift') playShift();
      }
    }
    prevGameRef.current = gameState;
  }, [gameState, playWin, playDraw, playPlace, playShift]);

  useEffect(() => {
    if (
      gameMode === 'ai' &&
      gameState &&
      !gameState.winner &&
      !gameState.isDraw &&
      gameState.currentPlayer !== playerRole &&
      !isAIThinking
    ) {
      const aiPlayer = playerRole === 'X' ? 'O' : 'X';
      dispatch(makeAIMove({ difficulty, aiPlayer }));
    }
  }, [gameMode, gameState, playerRole, isAIThinking, difficulty, dispatch]);

  useEffect(() => {
    if (
      !gameState ||
      gameState.phase !== 'shift' ||
      gameState.winner ||
      gameState.isDraw
    ) {
      return;
    }
    if (canPlayerShift(gameState, gameState.currentPlayer)) return;

    const resolved = resolveBlockedShiftTurn(gameState);
    if (
      resolved.currentPlayer === gameState.currentPlayer &&
      resolved.isDraw === gameState.isDraw
    ) {
      return;
    }

    dispatch(syncGameState(resolved));
    if (isLobbyPvp) {
      pushPvpStateToFirestore(resolved);
    }
  }, [gameState, dispatch, isLobbyPvp, pushPvpStateToFirestore]);

  const handleCellClick = useCallback(
    (index) => {
      if (!canMakeMove || !gameState) return;

      const isLocalPvp = gameMode === 'offline';
      const currentPlayer = isLocalPvp ? gameState.currentPlayer : playerRole;

      if (
        (isLobbyPvp || gameMode === 'online' || gameMode === 'pvp') &&
        gameState.currentPlayer !== playerRole
      ) {
        return;
      }

      if (gameState.phase === 'placement') {
        if (gameState.board[index] !== null) {
          playInvalid();
          return;
        }
        dispatch(placePiece({ position: index, player: currentPlayer }));
        if (isLobbyPvp) {
          pushPvpStateToFirestore(store.getState().shiftGame?.gameState);
        }
      }
    },
    [canMakeMove, gameState, gameMode, isLobbyPvp, playerRole, dispatch, playInvalid, pushPvpStateToFirestore]
  );

  const handleShiftDrop = useCallback(
    (from, to) => {
      if (!canMakeMove || !gameState || gameState.phase !== 'shift') return;

      const isLocalPvp = gameMode === 'offline';
      const currentPlayer = isLocalPvp ? gameState.currentPlayer : playerRole;

      if (
        (isLobbyPvp || gameMode === 'online' || gameMode === 'pvp') &&
        gameState.currentPlayer !== playerRole
      ) {
        return;
      }

      if (gameState.board[from] === null) {
        playInvalid();
        return;
      }

      playShift();
      dispatch(
        shiftPiece({
          from,
          to,
          player: currentPlayer,
        }),
      );
      if (isLobbyPvp) {
        pushPvpStateToFirestore(store.getState().shiftGame?.gameState);
      }
    },
    [
      canMakeMove,
      gameState,
      gameMode,
      isLobbyPvp,
      playerRole,
      dispatch,
      playShift,
      playInvalid,
      pushPvpStateToFirestore,
    ],
  );

  const executeAutoTurn = useCallback(() => {
    const rootState = store.getState();
    const liveState = rootState.shiftGame?.gameState;
    const liveMode = rootState.shiftGame?.gameMode;
    const liveRole = rootState.shiftGame?.playerRole;

    if (!liveState || !selectCanMakeMove(rootState)) return;

    const isLocalPvp = liveMode === 'offline';
    const currentPlayer = isLocalPvp ? liveState.currentPlayer : liveRole;

    if (
      (isLobbyPvp || liveMode === 'online' || liveMode === 'pvp' || liveMode === 'ai') &&
      liveState.currentPlayer !== liveRole
    ) {
      return;
    }

    if (liveState.phase === 'shift') {
      const move = pickRandomTurnMove(liveState, currentPlayer);
      if (move?.type === 'shift') {
        dispatch(
          shiftPiece({
            from: move.from,
            to: move.to,
            player: currentPlayer,
          }),
        );
        if (isLobbyPvp) {
          pushPvpStateToFirestore(store.getState().shiftGame?.gameState);
        }
        return;
      }

      const resolved = resolveBlockedShiftTurn(liveState);
      if (
        resolved.currentPlayer !== liveState.currentPlayer ||
        resolved.isDraw !== liveState.isDraw
      ) {
        dispatch(syncGameState(resolved));
        if (isLobbyPvp) {
          pushPvpStateToFirestore(resolved);
        }
      }
      return;
    }

    if (liveState.phase === 'placement') {
      const move = pickRandomTurnMove(liveState, currentPlayer);
      if (move?.type === 'place') {
        dispatch(placePiece({ position: move.position, player: currentPlayer }));
        if (isLobbyPvp) {
          pushPvpStateToFirestore(store.getState().shiftGame?.gameState);
        }
      }
    }
  }, [isLobbyPvp, dispatch, pushPvpStateToFirestore]);

  const isGameOverForTimer = Boolean(gameState?.winner || gameState?.isDraw);
  const isHumanTurn =
    gameMode === 'offline' ||
    (gameState && gameState.currentPlayer === playerRole);
  const turnCountdownEnabled =
    Boolean(gameState) &&
    (gameState.phase === 'placement' || gameState.phase === 'shift') &&
    canMakeMove &&
    !isGameOverForTimer &&
    !isAIThinking &&
    isHumanTurn;
  const turnCountdownKey = gameState
    ? gameState.phase === 'placement'
      ? `${gameState.currentPlayer}-placement-${gameState.moveHistory?.length ?? 0}`
      : `${gameState.currentPlayer}-shift-${gameState.moveHistory?.length ?? 0}-sel-${gameState.selectedPiece ?? 'none'}`
    : '';

  const { percent: turnCountdownPercent, secondsLeft: turnSecondsLeft } =
    useTurnCountdown({
      enabled: turnCountdownEnabled,
      paused: showRules,
      turnKey: turnCountdownKey,
      onExpire: executeAutoTurn,
    });

  const handleTurnTransitionReady = useCallback(() => {
    dispatch(dismissTurnTransition());
  }, [dispatch]);

  const handleCreateRoom = async () => {
    setOnlineView('creating');
    const result = await createRoom(settings);
    if (result.success) setOnlineView('waiting');
    else setOnlineView('menu');
  };

  const handleJoinRoom = async () => {
    if (joinCode.length !== 4) return;
    setOnlineView('joining');
    const result = await joinRoom(joinCode);
    if (!result.success) setOnlineView('join');
  };

  const localHudName = useMemo(
    () => resolvePlayerDisplayName({ name: hudLocalIdentity.name }) || 'Player',
    [hudLocalIdentity.name]
  );
  const localHudCharacterSrc = useMemo(
    () => characterImageSrc(hudLocalIdentity.character),
    [hudLocalIdentity.character]
  );
  const aiHudCharacterSrc = useMemo(() => aiOpponentImageSrc(), []);
  const hudPlayerNames = useMemo(() => {
    if (isPassPlay) {
      return {
        x: String(passPlayNames?.xName ?? '').trim() || 'Player X',
        o: String(passPlayNames?.oName ?? '').trim() || 'Player O',
        xCharacterImageSrc: characterImageSrc(passPlayNames?.xCharacter),
        oCharacterImageSrc: characterImageSrc(passPlayNames?.oCharacter),
      };
    }

    if (isLobbyPvp) {
      const xEntry = hudLobbyIdentities.x;
      const oEntry = hudLobbyIdentities.o;
      return {
        x: resolvePlayerDisplayName({ name: xEntry?.name }) || 'Player',
        o: resolvePlayerDisplayName({ name: oEntry?.name }) || 'Player',
        xCharacterImageSrc: characterImageSrc(xEntry?.character),
        oCharacterImageSrc: characterImageSrc(oEntry?.character),
      };
    }

    const role = playerRole === 'O' ? 'O' : 'X';
    if (gameMode === 'ai') {
      return role === 'X'
        ? {
            x: localHudName,
            o: 'Computer',
            xCharacterImageSrc: localHudCharacterSrc,
            oCharacterImageSrc: aiHudCharacterSrc,
          }
        : {
            x: 'Computer',
            o: localHudName,
            xCharacterImageSrc: aiHudCharacterSrc,
            oCharacterImageSrc: localHudCharacterSrc,
          };
    }

    const opponentName = gameMode === 'online' ? 'Opponent' : 'Player';
    return role === 'X'
      ? {
          x: localHudName,
          o: opponentName,
          xCharacterImageSrc: localHudCharacterSrc,
          oCharacterImageSrc: null,
        }
      : {
          x: opponentName,
          o: localHudName,
          xCharacterImageSrc: null,
          oCharacterImageSrc: localHudCharacterSrc,
        };
  }, [
    isPassPlay,
    isLobbyPvp,
    passPlayNames,
    playerRole,
    gameMode,
    localHudName,
    localHudCharacterSrc,
    aiHudCharacterSrc,
    hudLobbyIdentities,
  ]);

  if (!categoryMode) {
    return (
      <div className="stt-container game-page game-page--scroll">
        <p className="game-page__message">No game mode selected.</p>
        <button type="button" className="stt-btn stt-btn--primary" onClick={() => navigate('/category')}>
          Back to Category
        </button>
      </div>
    );
  }

  if (categoryMode === 'online' && !opponentJoined) {
    return (
      <div className="stt-container game-page game-page--scroll">
        <div className="stt-lobby stt-screen-enter">
          <h1 className="stt-lobby__title">{MODE_LABELS.online}</h1>

          {onlineView === 'menu' && (
            <div className="stt-lobby__options">
              <button type="button" className="stt-mode-card" onClick={handleCreateRoom}>
                <div className="stt-mode-card__icon">🏠</div>
                <div className="stt-mode-card__content">
                  <div className="stt-mode-card__title">Create Room</div>
                  <div className="stt-mode-card__desc">Get a code for your friend</div>
                </div>
              </button>
              <button type="button" className="stt-mode-card" onClick={() => setOnlineView('join')}>
                <div className="stt-mode-card__icon">🚪</div>
                <div className="stt-mode-card__content">
                  <div className="stt-mode-card__title">Join Room</div>
                  <div className="stt-mode-card__desc">Enter a 4-character code</div>
                </div>
              </button>
            </div>
          )}

          {(onlineView === 'creating' || onlineView === 'joining') && (
            <div className="stt-room-code">
              <div className="stt-spinner" />
              <div className="stt-room-code__status">
                {onlineView === 'creating' ? 'Creating room...' : 'Joining room...'}
              </div>
            </div>
          )}

          {onlineView === 'waiting' && (
            <div className="stt-room-code">
              <div className="stt-room-code__label">Room Code</div>
              <div className="stt-room-code__value">{roomCode}</div>
              <div className="stt-room-code__status">
                {connectionStatus === 'connected'
                  ? 'Waiting for opponent...'
                  : 'Connecting...'}
              </div>
              <div className="stt-spinner" style={{ marginTop: '1rem' }} />
            </div>
          )}

          {onlineView === 'join' && (
            <div className="stt-join-input">
              <input
                type="text"
                className="stt-input"
                placeholder="CODE"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase().slice(0, 4))}
                maxLength={4}
                autoFocus
              />
              {(mpError || error) && <div className="stt-error">{mpError || error}</div>}
              <button
                type="button"
                className="stt-btn stt-btn--primary"
                onClick={handleJoinRoom}
                disabled={joinCode.length !== 4}
              >
                Join Game
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (isLobbyPvp && (lobbyPvp.error || !lobbyContext.sessionId || !lobbyContext.lobbyName)) {
    return (
      <div className="stt-container game-page game-page--scroll">
        <p className="game-page__message">
          {lobbyPvp.error || 'Missing lobby session. Return to the waiting room.'}
        </p>
        <button type="button" className="stt-btn stt-btn--primary" onClick={() => navigate('/waiting')}>
          Back to Waiting
        </button>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="stt-container game-page game-page--scroll">
        <div className="game-page__loader" aria-busy="true" aria-live="polite">
          <div className="stt-spinner" aria-hidden="true" />
          {isLobbyPvp && !lobbyPvp.ready ? (
            <p className="game-page__message">Connecting to game room…</p>
          ) : null}
        </div>
      </div>
    );
  }

  const isLocalPvp = gameMode === 'offline';
  const isGameOver = gameState.winner || gameState.isDraw;
  const boardDisabled = !canMakeMove || isGameOver;
  const boardGameMode = isLocalPvp ? 'offline' : gameMode;
  const phaseLabel =
    gameState.phase === 'placement' ? 'Placement Phase' : 'Shift Phase';

  const showTurnStatus =
    Boolean(gameState) &&
    !isGameOver &&
    !showTurnTransition &&
    !isAIThinking;

  const isYourTurn = Boolean(showTurnStatus && canMakeMove);

  let turnStatusMessage = null;
  if (showTurnStatus && gameState) {
    if (isLocalPvp) {
      const name =
        gameState.currentPlayer === 'O'
          ? String(passPlayNames?.oName ?? '').trim() || 'Player O'
          : String(passPlayNames?.xName ?? '').trim() || 'Player X';
      turnStatusMessage = `${name}'s turn`;
    } else if (isYourTurn) {
      turnStatusMessage = 'Your turn';
    } else if (gameMode === 'ai') {
      turnStatusMessage = "Computer's turn";
    } else if (gameMode === 'pvp' || gameMode === 'online') {
      turnStatusMessage = "Opponent's turn";
    }
  }

  return (
    <div className="stt-container game-page">
      <div className="game-page__grid game-page__grid--top" aria-hidden="true" />
      <div className="game-page__grid game-page__grid--bottom" aria-hidden="true" />

      <div className="game-page__viewport stt-screen-enter">
        <div className="game-page__header">
          <button
            type="button"
            className="game-page__icon-btn game-page__slot--rules"
            onClick={() => setShowRules(true)}
            aria-label="Game rules"
          >
            ?
          </button>
          <div className="game-page__header-status">
            <p className="game-hud__phase-badge">{phaseLabel}</p>
            {turnStatusMessage ? (
              <p
                className={`game-page__turn-status${
                  isYourTurn ? ' game-page__turn-status--yours' : ''
                }`}
                role="status"
                aria-live="polite"
              >
                {turnStatusMessage}
              </p>
            ) : null}
          </div>
        </div>

        <GameHudXPanel
          gameState={gameState}
          isAIThinking={isAIThinking}
          isLocalPvp={isLocalPvp}
          playerRole={playerRole}
          passPlayNames={passPlayNames}
          xDisplayName={hudPlayerNames.x}
          oDisplayName={hudPlayerNames.o}
          xCharacterImageSrc={hudPlayerNames.xCharacterImageSrc}
          oCharacterImageSrc={hudPlayerNames.oCharacterImageSrc}
          showTurnTimer={turnCountdownEnabled}
          turnCountdownPercent={turnCountdownPercent}
          turnSecondsLeft={turnSecondsLeft}
        />

        <div className="game-page__board-column">
          <div className="game-page__board-area">
            <div className="game-page__board-wrap">
              <Board
                board={gameState.board}
                phase={gameState.phase}
                currentPlayer={gameState.currentPlayer}
                selectedPiece={gameState.selectedPiece}
                winningLine={gameState.winningLine}
                allowDiagonal={gameState.allowDiagonal}
                isDisabled={boardDisabled}
                playerRole={playerRole}
                gameMode={boardGameMode}
                onCellClick={handleCellClick}
                onShiftDrop={handleShiftDrop}
              />
            </div>
          </div>

          <GameHudOPanel
            gameState={gameState}
            isAIThinking={isAIThinking}
            isLocalPvp={isLocalPvp}
            playerRole={playerRole}
            passPlayNames={passPlayNames}
            xDisplayName={hudPlayerNames.x}
            oDisplayName={hudPlayerNames.o}
            xCharacterImageSrc={hudPlayerNames.xCharacterImageSrc}
            oCharacterImageSrc={hudPlayerNames.oCharacterImageSrc}
            showTurnTimer={turnCountdownEnabled}
            turnCountdownPercent={turnCountdownPercent}
            turnSecondsLeft={turnSecondsLeft}
          />
        </div>

        <div className="game-page__footer game-page__slot--footer">
        {gameState.phase === 'shift' && !isGameOver && (
          <p className="game-page__shift-hint">
            Shift phase — {gameState.currentPlayer}&apos;s turn: drag your{' '}
            {gameState.currentPlayer === 'X' ? 'cross' : 'nought'} to any empty cell
          </p>
        )}

        {error && (
          <div className="stt-error" role="alert">
            {error}
          </div>
        )}

        {isAIThinking && (
          <p className="game-page__shift-hint">Computer is thinking…</p>
        )}
        </div>
      </div>

      <RulesModal
        open={showRules}
        onClose={() => setShowRules(false)}
        rules={themeRules}
        title={rulesTitle}
        textStyle={rulesTextStyle}
        themeColors={themeColors}
      />

      {showTurnTransition && isLocalPvp && (
        <TurnTransition
          currentPlayer={gameState.currentPlayer}
          isIntro={gameState.moveHistory.length === 0}
          onReady={handleTurnTransitionReady}
          playerDisplayName={
            gameState.currentPlayer === 'X'
              ? passPlayNames?.xName
              : passPlayNames?.oName
          }
        />
      )}

    </div>
  );
}

export default Game;
